import { forwardRef, isValidElement, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/utils';

/**
 * Dark bubble, 11px, 300ms — the delay matters: at ERP density the pointer
 * crosses a dozen icon buttons on its way somewhere, and an instant tooltip
 * turns that into a strobe.
 */

/** Logical sides, so a caller never has to know which way the document runs. */
export type TooltipSide = 'top' | 'bottom' | 'start' | 'end';

type ContentProps = ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>;

// `content` is omitted because React's HTMLAttributes already carries an RDFa
// `content` string; ours is the bubble's children.
export interface TooltipProps extends Omit<ContentProps, 'children' | 'side' | 'content'> {
  /** Arabic copy — the whole point of the component. */
  content: ReactNode;
  /** A single element that can take a ref; anything else is wrapped in a span. */
  children: ReactNode;
  side?: TooltipSide;
  delayDuration?: number;
  /** Renders the child bare — for a control whose tooltip only applies sometimes. */
  disabled?: boolean;
}

/** DS-50 — `start`/`end` are physical sides once they reach Radix's popper. */
function resolveSide(side: TooltipSide): 'top' | 'right' | 'bottom' | 'left' {
  if (side === 'top' || side === 'bottom') return side;
  const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
  if (side === 'start') return rtl ? 'right' : 'left';
  return rtl ? 'left' : 'right';
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
  {
    content,
    children,
    side = 'top',
    align = 'center',
    sideOffset = 5,
    delayDuration = 300,
    disabled = false,
    className,
    ...rest
  },
  ref,
) {
  if (disabled || content === null || content === undefined || content === '') {
    return <>{children}</>;
  }

  const trigger = isValidElement(children) ? children : <span className="inline-flex">{children}</span>;

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{trigger}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            ref={ref}
            side={resolveSide(side)}
            align={align}
            sideOffset={sideOffset}
            className={cn(
              'z-50 max-w-xs animate-zoomIn rounded border border-slate-700 bg-slate-800',
              'px-2 py-1 text-[11px] leading-snug text-white shadow-lg',
              className,
            )}
            {...rest}
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-slate-800" width={9} height={4} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
});
